// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { formId } from "@conklin/Overrides/Widgets/ContactUs/ContactForm";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const currentBillToState = getCurrentBillToState(state);
    return {
        customerId: state.components.contactUsForm.fieldValues["customerId"],
        isAuthenticated: session.isAuthenticated && !session.isGuest,
        shouldLoadBillTo: !currentBillToState.value && !currentBillToState.isLoading,
        customerNumber: currentBillToState.value?.customerNumber,
    };
};

const mapDispatchToProps = {
    setFieldValue,
    loadCurrentBillTo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CustomerIdStyles {
    customerIdTextField?: TextFieldPresentationProps;
}

export const customerIdStyles: CustomerIdStyles = {};

const styles = customerIdStyles;

const CustomerId: React.FC<Props> = ({
    fields,
    customerId,
    isAuthenticated,
    shouldLoadBillTo,
    customerNumber,
    setFieldValue,
    loadCurrentBillTo,
}) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [customerIdErrorMessage, setCustomerIdErrorMessage] = React.useState("");

    React.useEffect(() => {
        if (isAuthenticated && shouldLoadBillTo) {
            loadCurrentBillTo();
        }
    }, [isAuthenticated, shouldLoadBillTo]);

    React.useEffect(() => {
        if (!customerId && customerNumber) {
            setFieldValue({ key: "customerId", value: customerNumber });
        }
    }, [customerId, customerNumber]);

    React.useEffect(() => {
        contactFormContext.validators.customerId = validateCustomerId;
        if (typeof customerId !== "undefined") {
            validateCustomerId();
        }
    }, [customerId]);

    React.useEffect(() => {
        contactFormContext.validators.customerId = validateCustomerId;
    }, [isRequired]);

    const validateCustomerId = () => {
        const errorMessage = isRequired && !customerId ? (siteMessage("Field_Required", label) as string) : "";
        setCustomerIdErrorMessage(errorMessage);
        return !errorMessage;
    };

    const customerIdChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "customerId", value: event.target.value });
    };

    return (
        <TextField
            {...styles.customerIdTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={customerId || ""}
            error={customerIdErrorMessage}
            onChange={customerIdChangeHandler}
            type="text"
            {...{ formId }}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CustomerId),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Customer ID",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Enter Customer ID",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
